import { useEffect, useRef } from "react";

type RevealVariant = "rise" | "wipe";

type RevealOptions = {
  /** Retardo entre elementos consecutivos, en ms. */
  step?: number;
  /** `rise` sube y funde. `wipe` barre lateralmente, como un instrumento midiendo. */
  variant?: RevealVariant;
};

/**
 * Entrada por scroll con IntersectionObserver.
 *
 * Devuelve una ref para el contenedor. Los descendientes con la clase `.reveal`
 * entran escalonadamente cuando aparecen en pantalla.
 *
 * Dos decisiones importantes:
 *
 * 1. **El contenido se sirve visible.** `.reveal` no oculta nada por sí sola. Este
 *    hook sólo "arma" (`.reveal-armed`) los elementos que están realmente bajo el
 *    pliegue en el momento del montaje. Así no hay parpadeo en lo que ya se ve, y
 *    sin JavaScript, en SSR o en un render headless la página se ve entera.
 * 2. **Sin listeners de scroll.** Un observer que se desconecta por elemento.
 *
 * El escalonado viaja por la variable CSS `--reveal-delay`, de modo que la animación
 * vive entera en CSS y React no vuelve a renderizar durante el scroll.
 */
export function useReveal<T extends HTMLElement = HTMLElement>({
  step = 80,
  variant = "rise",
}: RevealOptions = {}) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const items = Array.from(root.querySelectorAll<HTMLElement>(".reveal"));
    if (items.length === 0) return;

    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced || typeof IntersectionObserver === "undefined") return;

    // Sólo se arma lo que aún no se ve. Lo que ya está en pantalla se queda como está.
    const fold = window.innerHeight * 0.92;
    const armed = items.filter((el) => el.getBoundingClientRect().top > fold);
    if (armed.length === 0) return;

    armed.forEach((el) => {
      el.classList.add("reveal-armed");
      if (variant === "wipe") el.classList.add("reveal-wipe");
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target as HTMLElement;
          const index = armed.indexOf(el);
          el.style.setProperty("--reveal-delay", `${Math.max(index, 0) * step}ms`);
          el.classList.add("is-visible");
          observer.unobserve(el);
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -6% 0px" },
    );

    armed.forEach((el) => observer.observe(el));

    // Red de seguridad. Si por lo que sea el observer no llega a disparar sobre
    // un elemento que ya está en pantalla (una captura headless que no hace
    // scroll, un recorte que anula su caja), se muestra por geometría. Nunca
    // puede quedarse una sección en blanco esperando a una clase.
    const safety = window.setTimeout(() => {
      armed.forEach((el) => {
        if (el.classList.contains("is-visible")) return;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.classList.add("is-visible");
          observer.unobserve(el);
        }
      });
    }, 2000);

    return () => {
      window.clearTimeout(safety);
      observer.disconnect();
      // Si el efecto se rearma (StrictMode, cambio de opción), no dejar nada oculto.
      armed.forEach((el) => el.classList.remove("reveal-armed", "reveal-wipe"));
    };
  }, [step, variant]);

  return ref;
}
